/**
 * Geometry helpers for the Location map's gold outline.
 *
 * The outline is only ever a parcel (Regrid) or a building footprint (OpenStreetMap)
 * that a source returned for this place. `positionsOf` reads those; the approximate
 * marker below is the one shape the platform draws itself, and it is off.
 */
import type { GeoJsonGeometry } from '@/services/dossierContext'

/**
 * Draw a small gold square around the pin when no parcel or footprint exists.
 *
 * Off on purpose: a box with an edge length is a claim about where the lot is,
 * and no source supplied it. A pin says "about here" and nothing more. If it is
 * ever turned on, the map labels it as an approximation, not as a parcel.
 */
export const SHOW_APPROX_SITE_MARKER: boolean = false

/** Half the side of the approximate marker, in metres. */
export const APPROX_SITE_HALF_SIDE_M = 20

export const APPROX_SITE_LABEL = 'Approx. site marker - not a surveyed parcel'

/** Every [lng, lat] pair in a Polygon or MultiPolygon, for a bounding box. */
export function positionsOf(geometry: GeoJsonGeometry): [number, number][] {
  const out: [number, number][] = []
  const walk = (node: unknown) => {
    if (!Array.isArray(node)) return
    if (node.length >= 2 && typeof node[0] === 'number' && typeof node[1] === 'number') {
      out.push([node[0], node[1]])
      return
    }
    for (const child of node) walk(child)
  }
  walk(geometry.coordinates)
  return out
}

/** A square of `2 * halfSideM` centred on the point, as a GeoJSON Polygon. */
export function approxSiteMarker(
  lat: number,
  lng: number,
  halfSideM: number = APPROX_SITE_HALF_SIDE_M,
): GeoJsonGeometry {
  const metresPerDegLat = 111_320
  const dLat = halfSideM / metresPerDegLat
  const dLng = halfSideM / (metresPerDegLat * Math.cos((lat * Math.PI) / 180))
  return {
    type: 'Polygon',
    coordinates: [
      [
        [lng - dLng, lat - dLat],
        [lng + dLng, lat - dLat],
        [lng + dLng, lat + dLat],
        [lng - dLng, lat + dLat],
        [lng - dLng, lat - dLat],
      ],
    ],
  }
}
